import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Minus, Trash2, Gift, ShoppingBag } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart, FREE_BOTTLE_THRESHOLD } from "../lib/cart";
import { useLang } from "../lib/i18n";
import { OliveLogo } from "./OliveLogo";
import { CATALOG_DATA } from "./CategoryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FreeGiftSelector = () => {
  const { t } = useLang();
  const { freeBottle, setFreeBottle } = useCart();

  // Suggested gift options: smallest bottle from key categories.
  // CATALOG_DATA shape: { [categoryKey]: { products: [{id,name,image,size,price,tier,...}] } }
  const options = useMemo(() => {
    const picks = [
      { categoryKey: "oil",   productIdx: 0 },
      { categoryKey: "raki",  productIdx: 0 },
      { categoryKey: "wine",  productIdx: 0 },
      { categoryKey: "honey", productIdx: 0 },
    ];
    return picks
      .map(({ categoryKey, productIdx }) => {
        const cat = CATALOG_DATA[categoryKey];
        if (!cat || !Array.isArray(cat.products) || !cat.products[productIdx]) {
          return null;
        }
        const product = cat.products[productIdx];
        return {
          id: `gift-${categoryKey}-${product.tier || product.id}`,
          categoryKey,
          productName: product.name,
          size: product.size,
          tier: product.tier || "small",
          unitPrice: product.price,
        };
      })
      .filter(Boolean);
  }, []);

  return (
    <div
      data-testid="cart-free-gift"
      className="border border-[color:var(--goldenelia-gold)]/50 p-5 bg-[#0a0805] my-6"
    >
      <div className="flex items-center gap-3 mb-3">
        <Gift size={18} className="text-[color:var(--goldenelia-gold)]" strokeWidth={1.4} />
        <p className="font-display text-[10px] tracking-[0.45em] text-[color:var(--goldenelia-gold)] uppercase">
          {t("cart.giftEyebrow")}
        </p>
      </div>
      <p className="font-serif-luxury italic text-[color:var(--goldenelia-cream)] text-base mb-4 leading-snug">
        {t("cart.giftTitle")}
      </p>

      <div className="grid grid-cols-2 gap-2">
        {options.map((o) => {
          const selected = freeBottle?.id === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => setFreeBottle(selected ? null : o)}
              data-testid={`cart-gift-option-${o.categoryKey}`}
              className={`text-left p-3 border transition-colors duration-200 ${
                selected
                  ? "border-[color:var(--goldenelia-gold)] bg-[color:var(--goldenelia-gold)]/12"
                  : "border-[color:var(--goldenelia-gold)]/20 hover:border-[color:var(--goldenelia-gold)]/55"
              }`}
            >
              <p className="font-body text-[12px] text-[color:var(--goldenelia-cream)] leading-tight">
                {o.productName}
              </p>
              <p className="font-display text-[9px] tracking-[0.25em] text-[color:var(--goldenelia-gold)]/85 uppercase mt-1">
                {o.size}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CheckoutForm = ({ onBack, onSuccess }) => {
  const { t } = useLang();
  const { items, freeBottle, subtotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    postal_code: "",
    notes: "",
    gift_wrap: false,
    gift_message: "",
    recipient_name: "",
    discount_code: "",
  });
  const [shippingPreview, setShippingPreview] = useState(null);
  const [discountPreview, setDiscountPreview] = useState(null);
  const [discountError, setDiscountError] = useState(null);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // Live shipping preview when postal code changes
  React.useEffect(() => {
    if (!form.postal_code || form.postal_code.length < 4) {
      setShippingPreview(null);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API}/shipping/quote`,
          { postal_code: form.postal_code, country: "GR", subtotal },
          { signal: ctrl.signal }
        );
        setShippingPreview(data);
      } catch { /* ignore */ }
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [form.postal_code, subtotal]);

  const applyDiscount = async () => {
    setDiscountError(null);
    setDiscountPreview(null);
    if (!form.discount_code.trim()) return;
    try {
      const { data } = await axios.post(`${API}/discount/validate`, {
        code: form.discount_code,
        subtotal,
      });
      setDiscountPreview(data);
      toast.success(`-€${data.amount.toFixed(2)} (${data.code})`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Μη έγκυρος κωδικός";
      setDiscountError(msg);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.address || !form.city) {
      toast.error(t("order.errors.required"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        country: "GR",
        // Pydantic EmailStr rejects empty strings — coerce empty → null
        email: form.email && form.email.trim() ? form.email.trim() : null,
        gift_message: form.gift_message && form.gift_message.trim() ? form.gift_message : null,
        recipient_name: form.recipient_name && form.recipient_name.trim() ? form.recipient_name : null,
        discount_code: form.discount_code && form.discount_code.trim() ? form.discount_code.trim() : null,
        postal_code: form.postal_code && form.postal_code.trim() ? form.postal_code.trim() : null,
        notes: form.notes && form.notes.trim() ? form.notes : null,
        items: items.map((it) => ({
          category_key: it.categoryKey,
          product_name: it.productName,
          size: it.size,
          tier: it.tier,
          unit_price: it.unitPrice,
          qty: it.qty,
        })),
        free_bottle: freeBottle
          ? {
              category_key: freeBottle.categoryKey,
              product_name: freeBottle.productName,
              size: freeBottle.size,
              tier: freeBottle.tier,
              unit_price: 0,
              qty: 1,
            }
          : null,
      };
      const res = await axios.post(`${API}/cart-orders`, payload);
      toast.success(t("cart.checkoutSuccess") + ` · #${res.data.id.slice(0, 8)}`);
      clearCart();
      onSuccess(res.data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t("order.errors.generic");
      toast.error(typeof msg === "string" ? msg : t("order.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const f = t("order.fields");

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="cart-checkout-form">
      <h3 className="font-serif-luxury text-2xl gold-gradient-text mb-2">
        {t("cart.checkoutTitle")}
      </h3>
      <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/55 mb-4">
        {t("cart.checkoutSub")}
      </p>

      <input required value={form.full_name} onChange={onChange("full_name")} placeholder={f.name} data-testid="checkout-name" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-3 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      <input required value={form.phone} onChange={onChange("phone")} placeholder={f.phone} data-testid="checkout-phone" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-3 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      <input type="email" value={form.email} onChange={onChange("email")} placeholder={f.email} data-testid="checkout-email" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-3 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      <input required value={form.address} onChange={onChange("address")} placeholder={f.address} data-testid="checkout-address" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-3 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      <div className="grid grid-cols-2 gap-3">
        <input required value={form.city} onChange={onChange("city")} placeholder={f.city} data-testid="checkout-city" className="bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-3 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
        <input value={form.postal_code} onChange={onChange("postal_code")} placeholder={f.postal} data-testid="checkout-postal" className="bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-3 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none" />
      </div>
      <textarea value={form.notes} onChange={onChange("notes")} placeholder={f.notes} rows={2} data-testid="checkout-notes" className="w-full bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-3 text-sm text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none resize-none" />

      {/* Gift wrap + Personalised message */}
      <div className="border border-[color:var(--goldenelia-gold)]/25 p-3 space-y-3">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="checkout-giftwrap-label">
          <input
            type="checkbox"
            checked={form.gift_wrap}
            onChange={onChange("gift_wrap")}
            data-testid="checkout-giftwrap"
            className="mt-1 accent-[color:var(--goldenelia-gold)] w-4 h-4"
          />
          <span className="flex-1">
            <span className="font-display text-[11px] tracking-[0.25em] uppercase text-[color:var(--goldenelia-gold)]">
              🎁 Premium Gift Wrap · +€5
            </span>
            <span className="block font-body text-[11px] text-[color:var(--goldenelia-cream)]/55 mt-0.5">
              Μαύρο πολυτελές κουτί με χρυσή κορδέλα Golden Elia
            </span>
          </span>
        </label>
        {form.gift_wrap && (
          <div className="space-y-2 pl-7">
            <input
              value={form.recipient_name}
              onChange={onChange("recipient_name")}
              placeholder="Όνομα παραλήπτη (προαιρετικό)"
              data-testid="checkout-recipient"
              className="w-full bg-black border border-[color:var(--goldenelia-gold)]/30 px-3 py-2 text-xs text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none"
            />
            <textarea
              value={form.gift_message}
              onChange={onChange("gift_message")}
              maxLength={500}
              placeholder="Προσωπικό μήνυμα στην κάρτα (έως 500 χαρ.)"
              rows={2}
              data-testid="checkout-giftmsg"
              className="w-full bg-black border border-[color:var(--goldenelia-gold)]/30 px-3 py-2 text-xs text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none resize-none"
            />
          </div>
        )}
      </div>

      {/* Discount code */}
      <div className="flex gap-2">
        <input
          value={form.discount_code}
          onChange={onChange("discount_code")}
          placeholder="Κωδικός έκπτωσης"
          data-testid="checkout-discount"
          className="flex-1 bg-black border border-[color:var(--goldenelia-gold)]/30 px-4 py-2 text-xs text-[color:var(--goldenelia-cream)] focus:border-[color:var(--goldenelia-gold)] outline-none uppercase tracking-wider"
        />
        <button
          type="button"
          onClick={applyDiscount}
          data-testid="checkout-apply-discount"
          className="btn-ghost-gold !py-2 !px-4 text-[10px] whitespace-nowrap"
        >
          Εφαρμογή
        </button>
      </div>
      {discountError && (
        <p className="text-[11px] text-red-400/80 font-body" data-testid="checkout-discount-error">{discountError}</p>
      )}

      {/* Cost breakdown */}
      <div className="border-t border-[color:var(--goldenelia-gold)]/20 pt-3 space-y-1.5">
        <div className="flex justify-between text-xs text-[color:var(--goldenelia-cream)]/65 font-body">
          <span>Σύνολο προϊόντων</span>
          <span>€{subtotal.toFixed(2)}</span>
        </div>
        {shippingPreview && (
          <div className="flex justify-between text-xs text-[color:var(--goldenelia-cream)]/65 font-body" data-testid="checkout-shipping-line">
            <span>Μεταφορικά · {shippingPreview.zone_label}</span>
            <span>{shippingPreview.free_applied ? "ΔΩΡΕΑΝ" : `€${shippingPreview.cost.toFixed(2)}`}</span>
          </div>
        )}
        {form.gift_wrap && (
          <div className="flex justify-between text-xs text-[color:var(--goldenelia-cream)]/65 font-body">
            <span>🎁 Gift wrap</span>
            <span>€5.00</span>
          </div>
        )}
        {discountPreview && (
          <div className="flex justify-between text-xs font-body" style={{ color: "#9bd99b" }} data-testid="checkout-discount-line">
            <span>Έκπτωση ({discountPreview.code})</span>
            <span>-€{discountPreview.amount.toFixed(2)}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-[color:var(--goldenelia-gold)]/20">
        <span className="font-display text-xs tracking-[0.3em] uppercase text-[color:var(--goldenelia-cream)]/70">{t("order.total")}</span>
        <span className="gold-gradient-text font-serif-luxury text-2xl" data-testid="checkout-grand-total">
          €{(
            subtotal
            + (shippingPreview && !shippingPreview.free_applied ? shippingPreview.cost : 0)
            + (form.gift_wrap ? 5 : 0)
            - (discountPreview ? discountPreview.amount : 0)
          ).toFixed(2)}
        </span>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} data-testid="checkout-back" className="btn-ghost-gold !py-3 !px-5 text-[10px] flex-1">
          ← {t("cart.backToCart")}
        </button>
        <button type="submit" disabled={loading} data-testid="checkout-submit" className="btn-solid-gold !py-3 !px-5 text-[10px] flex-1">
          {loading ? t("order.sending") : t("order.submit")}
        </button>
      </div>
    </form>
  );
};

export const CartDrawer = () => {
  const { t } = useLang();
  const {
    items,
    isOpen,
    closeCart,
    updateQty,
    removeItem,
    subtotal,
    eligibleForFreeGift,
    freeBottle,
    openCheckoutToken,
  } = useCart();
  const [checkout, setCheckout] = useState(false);
  const [success, setSuccess] = useState(null);

  // Reset checkout state when closing
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => { setCheckout(false); setSuccess(null); }, 300);
    }
  }, [isOpen]);

  // Jump directly to checkout when "Buy now" is triggered
  const checkoutTokenRef = useRef(0);
  useEffect(() => {
    if (openCheckoutToken && openCheckoutToken !== checkoutTokenRef.current) {
      checkoutTokenRef.current = openCheckoutToken;
      setSuccess(null);
      setCheckout(true);
    }
  }, [openCheckoutToken]);

  const remaining = Math.max(0, FREE_BOTTLE_THRESHOLD - subtotal);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeCart}
        data-testid="cart-backdrop"
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 z-[150]" : "opacity-0 pointer-events-none -z-10"
        }`}
      />

      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[480px] bg-black border-l border-[color:var(--goldenelia-gold)]/35 z-[160] transition-transform duration-500 ease-out flex flex-col overflow-hidden ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Watermark olive tree logo behind everything */}
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          style={{ opacity: 0.05 }}
        >
          <OliveLogo size={420} />
        </div>
        {/* subtle gold radial glow */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 60% 35%, rgba(201,169,97,0.10) 0%, rgba(0,0,0,0) 60%)",
          }}
        />

        <div className="relative flex flex-col h-full">
        <header className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--goldenelia-gold)]/25">
          <div>
            <p className="font-display text-[10px] tracking-[0.5em] text-[color:var(--goldenelia-gold)] uppercase">
              Golden Elia
            </p>
            <h3 className="font-serif-luxury text-2xl gold-gradient-text italic">
              {t("cart.title")}
            </h3>
          </div>
          <button
            onClick={closeCart}
            data-testid="cart-close"
            className="text-[color:var(--goldenelia-gold)] hover:text-[color:var(--goldenelia-cream)] transition-colors"
            aria-label="close cart"
          >
            <X size={22} strokeWidth={1.4} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {success ? (
            <div data-testid="cart-success" className="text-center py-10">
              <p className="font-display text-[10px] tracking-[0.5em] text-[color:var(--goldenelia-gold)] uppercase mb-4">
                Golden Elia
              </p>
              <h4 className="font-serif-luxury text-3xl gold-gradient-text italic mb-3">
                {t("order.thanks")}
              </h4>
              <p className="font-body text-sm text-[color:var(--goldenelia-cream)]/65 mb-6 leading-relaxed">
                {t("order.subDone")}
              </p>
              <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/45">
                {t("order.orderNo")} #{success.id?.slice(0, 8).toUpperCase()}
              </p>
              <button onClick={closeCart} className="btn-ghost-gold !py-3 !px-6 text-[10px] mt-8">
                {t("order.close")}
              </button>
            </div>
          ) : checkout ? (
            <CheckoutForm onBack={() => setCheckout(false)} onSuccess={setSuccess} />
          ) : items.length === 0 ? (
            <div data-testid="cart-empty" className="text-center py-12">
              <ShoppingBag size={42} strokeWidth={1} className="text-[color:var(--goldenelia-gold)]/40 mx-auto mb-4" />
              <p className="font-serif-luxury italic text-lg text-[color:var(--goldenelia-cream)]/70 mb-2">
                {t("cart.emptyTitle")}
              </p>
              <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/45">
                {t("cart.emptyHint")}
              </p>
              <button onClick={closeCart} className="btn-ghost-gold !py-3 !px-6 text-[10px] mt-8">
                {t("cart.continueShopping")}
              </button>
            </div>
          ) : (
            <>
              <ul className="space-y-px bg-[color:var(--goldenelia-gold)]/15">
                {items.map((it) => (
                  <li
                    key={it.id}
                    data-testid={`cart-item-${it.id}`}
                    className="bg-black p-4 grid grid-cols-[1fr_auto] gap-3 items-start"
                  >
                    <div className="min-w-0">
                      <p className="font-body text-[10px] tracking-[0.3em] text-[color:var(--goldenelia-gold)]/80 uppercase mb-1 truncate">
                        {it.categoryName}
                      </p>
                      <p className="font-serif-luxury text-base text-[color:var(--goldenelia-cream)] leading-tight truncate">
                        {it.productName}
                      </p>
                      <p className="font-body text-[11px] text-[color:var(--goldenelia-cream)]/55 mt-1">
                        {it.size} · €{it.unitPrice.toFixed(2)}
                      </p>
                      <div className="flex items-center gap-3 mt-3">
                        <div className="inline-flex items-center border border-[color:var(--goldenelia-gold)]/35">
                          <button
                            onClick={() => updateQty(it.id, it.qty - 1)}
                            data-testid={`cart-dec-${it.id}`}
                            className="px-2 py-1 text-[color:var(--goldenelia-gold)] hover:bg-[color:var(--goldenelia-gold)]/10"
                            aria-label="decrease"
                          >
                            <Minus size={10} />
                          </button>
                          <span className="px-2 text-xs tabular-nums text-[color:var(--goldenelia-cream)] min-w-[2ch] text-center" data-testid={`cart-qty-${it.id}`}>
                            {it.qty}
                          </span>
                          <button
                            onClick={() => updateQty(it.id, it.qty + 1)}
                            data-testid={`cart-inc-${it.id}`}
                            className="px-2 py-1 text-[color:var(--goldenelia-gold)] hover:bg-[color:var(--goldenelia-gold)]/10"
                            aria-label="increase"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(it.id)}
                          data-testid={`cart-remove-${it.id}`}
                          className="text-[color:var(--goldenelia-cream)]/40 hover:text-[color:var(--goldenelia-gold)] transition-colors"
                          aria-label="remove"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <p className="gold-gradient-text font-serif-luxury text-lg whitespace-nowrap">
                      €{(it.unitPrice * it.qty).toFixed(2)}
                    </p>
                  </li>
                ))}
              </ul>

              {/* Progress to free gift */}
              {!eligibleForFreeGift && items.length > 0 && (
                <div data-testid="cart-progress" className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-display text-[10px] tracking-[0.35em] text-[color:var(--goldenelia-gold)]/80 uppercase">
                      {t("cart.almostThere")}
                    </p>
                    <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/55">
                      €{remaining.toFixed(2)} {t("cart.toGo")}
                    </p>
                  </div>
                  <div className="h-1 bg-[color:var(--goldenelia-gold)]/15 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#a78645] to-[#f4d98e] transition-all duration-500"
                      style={{ width: `${Math.min(100, (subtotal / FREE_BOTTLE_THRESHOLD) * 100)}%` }}
                    />
                  </div>
                  <p className="font-serif-luxury italic text-[13px] text-[color:var(--goldenelia-cream)]/70 mt-3 leading-snug">
                    {t("cart.unlock80")}
                  </p>
                </div>
              )}

              {/* Free gift selector */}
              {eligibleForFreeGift && <FreeGiftSelector />}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && !checkout && items.length > 0 && (
          <footer className="relative border-t border-[color:var(--goldenelia-gold)]/25 px-6 py-5 bg-black/85 backdrop-blur-sm">
            {freeBottle && (
              <div className="flex items-center gap-2 mb-3 text-[12px]" data-testid="cart-gift-included">
                <Gift size={14} className="text-[color:var(--goldenelia-gold)]" />
                <span className="text-[color:var(--goldenelia-cream)]/75">
                  {t("cart.giftIncluded")}: <span className="text-[color:var(--goldenelia-cream)]">{freeBottle.productName} · {freeBottle.size}</span>
                </span>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <span className="font-display text-[11px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-cream)]/70">
                {t("cart.subtotal")}
              </span>
              <span data-testid="cart-subtotal" className="gold-gradient-text font-serif-luxury text-2xl">
                €{subtotal.toFixed(2)}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={closeCart}
                data-testid="cart-continue"
                className="btn-ghost-gold !py-3 !px-4 text-[10px] flex-1"
              >
                {t("cart.continueShopping")}
              </button>
              <button
                onClick={() => setCheckout(true)}
                data-testid="cart-checkout"
                className="btn-solid-gold !py-3 !px-4 text-[10px] flex-[1.3]"
              >
                {t("cart.checkout")} →
              </button>
            </div>
          </footer>
        )}
        </div>
      </aside>
    </>
  );
};

export default CartDrawer;
